import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Mail, ChevronDown } from 'lucide-react';

const Hero: React.FC = () => {
  const [roleIndex, setRoleIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const roles = [
    { text: 'Full Stack Developer', color: '#2563eb' },
    { text: 'React Developer', color: '#7c3aed' },
    { text: 'TypeScript Expert', color: '#0284c7' },
    { text: 'Python Enthusiast', color: '#059669' },
    { text: 'Backend Engineer', color: '#2563eb' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setRoleIndex(prev => (prev + 1) % roles.length);
        setIsTransitioning(false);
      }, 500);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: '#ffffff', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >

      {/* Slow-moving large organic blobs */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <motion.div
          animate={{
            x: [0, 30, 0],
            y: [0, -40, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute rounded-full"
          style={{
            top: '-15%', left: '-10%', width: '70vw', height: '70vw', maxWidth: '800px', maxHeight: '800px',
            background: 'radial-gradient(circle, rgba(14, 165, 233, 0.15) 0%, transparent 60%)',
            filter: 'blur(90px)',
          }}
        />
        <motion.div
          animate={{
            x: [0, -50, 0],
            y: [0, 30, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute rounded-full"
          style={{
            top: '20%', right: '-15%', width: '60vw', height: '60vw', maxWidth: '700px', maxHeight: '700px',
            background: 'radial-gradient(circle, rgba(236, 72, 153, 0.1) 0%, transparent 60%)',
            filter: 'blur(100px)',
          }}
        />
        <motion.div
          animate={{
            x: [0, 40, 0],
            y: [0, 50, 0],
            scale: [1, 1.05, 1],
          }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute rounded-full"
          style={{
            bottom: '-20%', left: '20%', width: '80vw', height: '80vw', maxWidth: '900px', maxHeight: '900px',
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 60%)',
            filter: 'blur(120px)',
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-20 pb-32 md:pb-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Left: Text */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="order-2 lg:order-1 space-y-8 text-center lg:text-left"
          >
            {/* Elegant Typographic Intro */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="space-y-4"
            >
              <h2 
                className="text-lg md:text-xl font-medium tracking-wide uppercase"
                style={{ color: '#0ea5e9', fontFamily: "'Outfit', sans-serif" }}
              >
                Welcome to my universe
              </h2>
              
              {/* Name */}
              <h1
                className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                <span style={{ color: '#0f172a' }}>Hello, I'm</span>
                <br />
                <span
                  style={{
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 50%, #a855f7 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Amit Adhikary
                </span>
              </h1>
            </motion.div>

            {/* Sliding role - Liquid Glass style */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="flex items-center justify-center lg:justify-start"
            >
              <div
                className="relative overflow-hidden px-6 py-2 rounded-full shadow-lg"
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 1)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.05)',
                }}
              >
                <motion.span
                  key={roleIndex}
                  initial={{ opacity: 0, y: 15, filter: 'blur(4px)' }}
                  animate={{ opacity: isTransitioning ? 0 : 1, y: isTransitioning ? -15 : 0, filter: isTransitioning ? 'blur(4px)' : 'blur(0px)' }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  className="block text-lg font-medium text-center"
                  style={{ color: roles[roleIndex].color, fontFamily: "'Outfit', sans-serif" }}
                >
                  {roles[roleIndex].text}
                </motion.span>
              </div>
            </motion.div>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="text-base sm:text-lg leading-relaxed max-w-xl mx-auto lg:mx-0 font-light"
              style={{ color: '#475569' }}
            >
              Software Engineer & Full Stack Developer from India. I build{' '}
              <span style={{ color: '#0f172a', fontWeight: 500 }}>scalable, high-performance</span>{' '}
              applications with clean architecture and a passion for elegant solutions.
            </motion.p>

            {/* Action buttons - Pill-shaped liquid buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4"
            >
              {/* Primary CTA */}
              <motion.button
                onClick={() => scrollToSection('projects')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group flex items-center justify-center gap-2 px-8 py-4 rounded-full font-medium text-base transition-all duration-300"
                style={{
                  background: 'rgba(255, 255, 255, 0.7)',
                  border: '1px solid rgba(255, 255, 255, 1)',
                  color: '#0f172a',
                  boxShadow: '0 8px 32px rgba(14, 165, 233, 0.15)',
                  backdropFilter: 'blur(10px)',
                  fontFamily: "'Outfit', sans-serif"
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(255, 255, 255, 0.9)';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(14, 165, 233, 0.25)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(255, 255, 255, 0.7)';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(14, 165, 233, 0.15)';
                }}
              >
                <span>View My Work</span>
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </motion.button>

              {/* Secondary CTA */}
              <motion.button
                onClick={() => scrollToSection('contact')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center justify-center gap-2 px-8 py-4 rounded-full font-medium text-base transition-all duration-300"
                style={{
                  background: 'rgba(255, 255, 255, 0.4)',
                  border: '1px solid rgba(255, 255, 255, 0.8)',
                  color: '#475569',
                  backdropFilter: 'blur(10px)',
                  fontFamily: "'Outfit', sans-serif"
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(255, 255, 255, 0.8)';
                  (e.currentTarget as HTMLElement).style.color = '#0f172a';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(255, 255, 255, 0.4)';
                  (e.currentTarget as HTMLElement).style.color = '#475569';
                }}
              >
                <Mail size={18} />
                <span>Get In Touch</span>
              </motion.button>
            </motion.div>

            {/* Stats row — Elegant glass style */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3 }}
              className="flex items-center gap-8 sm:gap-12 justify-center lg:justify-start pt-6"
            >
              {[
                { value: '75+', label: 'Projects' },
                { value: '4500+', label: 'Problems Solved' },
                { value: '5+', label: 'Years Exp' },
              ].map((stat, i) => (
                <div key={i} className="text-center lg:text-left">
                  <div
                    className="text-2xl sm:text-3xl font-bold"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      background: 'linear-gradient(135deg, #0ea5e9, #8b5cf6)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-sm font-medium tracking-wide mt-1" style={{ color: '#475569' }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right: Profile image - Soft floating rounded glass container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
            className="flex justify-center lg:justify-end order-1 lg:order-2"
          >
            <div className="relative">
              {/* Ambient backdrop glow */}
              <div
                className="absolute inset-0 rounded-3xl"
                style={{
                  background: 'radial-gradient(circle, rgba(14, 165, 233, 0.15) 0%, transparent 70%)',
                  filter: 'blur(30px)',
                  transform: 'scale(1.2)',
                }}
              />

              {/* Floating glass container */}
              <motion.div
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                className="relative p-3 rounded-[2rem] sm:rounded-[2.5rem]"
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 1)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.05), inset 0 0 0 1px rgba(255, 255, 255, 0.8)',
                }}
              >
                <div
                  className="relative w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden"
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/images/My_photo.webp`}
                    alt="Amit Adhikary"
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    style={{ filter: 'brightness(1.02) saturate(1.05)' }}
                  />
                  
                  {/* Subtle inner shadow overlay */}
                  <div className="absolute inset-0 rounded-[1.5rem] sm:rounded-[2rem] shadow-[inset_0_0_20px_rgba(255,255,255,0.5)] pointer-events-none" />
                </div>

                {/* Floating glass badges */}
                {[
                  { text: 'React', top: '10%', right: '-15%', delay: 0, icon: '⚛️' },
                  { text: 'TypeScript', bottom: '15%', left: '-15%', delay: 1.5, icon: '📘' },
                  { text: 'Python', top: '60%', right: '-10%', delay: 3, icon: '🐍' },
                ].map((badge, i) => (
                  <motion.div
                    key={i}
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 5 + i, repeat: Infinity, ease: 'easeInOut', delay: badge.delay }}
                    whileHover={{ scale: 1.1 }}
                    className="absolute flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-medium cursor-default shadow-xl"
                    style={{
                      ...(badge.top ? { top: badge.top } : {}),
                      ...(badge.bottom ? { bottom: badge.bottom } : {}),
                      ...(badge.right ? { right: badge.right } : {}),
                      ...(badge.left ? { left: badge.left } : {}),
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(255, 255, 255, 1)',
                      color: '#0f172a',
                      backdropFilter: 'blur(16px)',
                      WebkitBackdropFilter: 'blur(16px)',
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}
                  >
                    <span>{badge.icon}</span>
                    <span>{badge.text}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator - elegant style */}
      <div className="absolute bottom-4 md:bottom-8 left-0 right-0 flex justify-center hidden md:flex">
        <motion.button
          onClick={() => scrollToSection('about')}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8 }}
          className="flex flex-col items-center gap-3 group"
        >
          <span
            className="text-xs tracking-[0.2em] uppercase font-medium transition-colors duration-300 group-hover:text-slate-800"
            style={{ color: '#475569', fontFamily: "'Outfit', sans-serif" }}
          >
            Scroll
          </span>
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            className="p-2 rounded-full"
            style={{ 
              background: 'rgba(255, 255, 255, 0.6)',
              border: '1px solid rgba(255, 255, 255, 1)',
              backdropFilter: 'blur(8px)'
            }}
          >
            <ChevronDown size={16} className="text-sky-500 group-hover:text-sky-600 transition-colors duration-300" />
          </motion.div>
        </motion.button>
      </div>
    </section>
  );
};

export default Hero;